import { cn } from '@/lib/utils'
import { useEffect, useMemo, useRef, useState } from 'react'
import ChatBottomBar from './chat-bottom-bar.tsx'
import { AnimatePresence, motion } from 'motion/react'
import useGlobalStore from '@/useGlobalStore.ts'
import { Chat, ChatEntry, LibraryGrammar, LibraryVocabulary, SendingChatEntry, User } from '@/models.ts'
import {
	ArrowDown,
	EllipsisVertical,
	Lightbulb,
	Loader2,
	LoaderCircle,
	Maximize2,
	Plus,
	PlusCircle,
	Rocket,
	Sparkles,
	Trash2,
	UserPlus,
} from 'lucide-react'
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card.tsx'
import { Badge } from '@/components/ui/badge.tsx'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button.tsx'
import useWordDetailsModalStore, { OnSuccessfulFinalActionMode } from '@/useWordDetailsModalStore.ts'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.tsx'
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog.tsx'
import { LoadingButton } from '@/components/loading-button.tsx'
import { Checkbox } from '@/components/ui/checkbox.tsx'
import api from '@/api.ts'
import { RainbowButton } from '@/components/ui/rainbow-button.tsx'
import DotPattern from '@/components/ui/dot-pattern.tsx'
import dayjs from 'dayjs'
import { dispatch } from '@/useBus.ts'
import { toast } from 'sonner'
import Markdown from 'react-markdown'
import { PhotoProvider, PhotoView } from 'react-photo-view'
import usePrevious from '@/usePrevious.ts'
import { Carousel, CarouselContent, CarouselDots, CarouselItem } from '@/components/ui/carousel.tsx'
import remarkBreaks from 'remark-breaks'

interface ChatListProps {
	chat: Chat | null
	entries: ChatEntry[]
	sendMessage: (newMessage: SendingChatEntry) => void
}

interface ChatEntryComponentProps {
	entry: ChatEntry
	user: User | null
}

const Message = ({ entry, user }: ChatEntryComponentProps) => {
	return (
		<>
			{entry.user_id !== user?.id && <Sparkles className="h-5 w-5" />}
			<div
				className={cn(
					'rounded-md max-w-xs xl:max-w-md py-1.5 px-3 relative text-start',
					entry.user_id === user?.id ? 'bg-tertiary' : '',
					entry.is_loading ? 'text-transparent bg-accent' : '',
				)}>
				<div dir="auto" className="m-0 -mb-2 leading-7 markdown selection:bg-gray-200 [unicode-bidi:plaintext]">
					<Markdown remarkPlugins={[remarkBreaks]}>{entry.message.text}</Markdown>
				</div>
				{entry.message?.attachments?.images.length > 0 && (
					<div className="mt-4 mb-1 flex">
						<PhotoProvider>
							{entry.message?.attachments?.images.map((image, index) => {
								return (
									<PhotoView key={index} src={image}>
										<img
											src={image}
											className="w-16 h-16 object-cover rounded-md me-2"
											alt="attached image"
										/>
									</PhotoView>
								)
							})}
						</PhotoProvider>
					</div>
				)}
			</div>
		</>
	)
}

const NewGrammar = ({ item, chatEntry }: { item: LibraryGrammar; chatEntry: ChatEntry }) => {
	const { t } = useTranslation()
	const { updateChatEntryByFieldName, loadLibraryOverview } = useGlobalStore()
	const [isLoading, setIsLoading] = useState<boolean>(false)

	const handleSaveOrRemove = async () => {
		setIsLoading(true)
		try {
			const data: LibraryGrammar = await api(`/v1/library/${!item.id ? 'add-grammar' : 'remove-grammar'}`, {
				method: item.id ? 'DELETE' : 'POST',
				body: item,
			}).catch((error) => {
				toast(error.data?.message ?? 'Operation failed')
			})
			const entry: ChatEntry = JSON.parse(JSON.stringify(chatEntry))
			if (data.client_id) {
				// successful addition
				entry.learning.grammar_knowledge.new = entry.learning.grammar_knowledge.new.filter(
					(x) => x.client_id !== data.client_id,
				)
				// todo: uncomment below to enable replacing add button with remove button instead of hiding the record
				// entry.learning.vocabulary_knowledge.new = entry.learning.vocabulary_knowledge.new.map(x =>
				// 	// @ts-ignore
				// 	x.client_id === data.client_id ? { ...x, id: data.id } : x
				// );
				entry.learning.grammar_knowledge.existing.push(data)
			} else {
				const removeId = (obj: any): any => {
					const objCopy = JSON.parse(JSON.stringify(obj))
					delete objCopy.id
					return objCopy
				}
				entry.learning.grammar_knowledge.new = entry.learning.grammar_knowledge.new.map((x) =>
					// @ts-ignore
					x.id === data.id ? removeId(x) : x,
				)
				entry.learning.grammar_knowledge.existing = entry.learning.grammar_knowledge.existing.filter(
					(x) => x.id !== data.id,
				)
			}
			setIsLoading(false)
			updateChatEntryByFieldName('id', entry.id, entry)
			await loadLibraryOverview()
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<div className="py-1 flex">
			<div className="w-3/5">
				<span className="text-base block font-normal">{item.grammar_topic}</span>
				{/*<small className="text-base text-muted-foreground">{item.translation}</small>*/}
			</div>
			<div className="w-2/5 flex items-center justify-center">
				<div className="flex items-center justify-center w-full">
					<button
						onClick={handleSaveOrRemove}
						className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background active:bg-accent active:text-accent-foreground p-0 h-9 w-full">
						{isLoading ? (
							<LoaderCircle className="animate-spin" width="18" />
						) : !item.id ? (
							<Plus id="addToLib" width="18" />
						) : (
							<Trash2 id="addToLib" width="18" />
						)}
						<label
							htmlFor="addToLib"
							className="text-sm ms-2 font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
							{!item.id ? t('Save') : t('Remove')}
						</label>
					</button>
				</div>
			</div>
		</div>
	)
}

const NewWord = ({ item, chatEntry }: { item: LibraryVocabulary; chatEntry: ChatEntry }) => {
	const { t } = useTranslation()
	const { updateChatEntryByFieldName, loadLibraryOverview } = useGlobalStore()
	const [isLoading, setIsLoading] = useState<boolean>(false)

	const handleSaveOrRemove = async () => {
		setIsLoading(true)
		try {
			const data: LibraryVocabulary = await api(`/v1/library/vocabulary/${item.id ? item.id : ''}`, {
				method: item.id ? 'DELETE' : 'POST',
				body: item,
			}).catch((error) => {
				toast(error.data?.message ?? 'Operation failed')
			})
			if (data === undefined) return
			const entry: ChatEntry = JSON.parse(JSON.stringify(chatEntry))
			if (data.client_id) {
				// successful addition
				entry.learning.vocabulary_knowledge.new = entry.learning.vocabulary_knowledge.new.filter(
					(x) => x.client_id !== data.client_id,
				)
				// todo: uncomment below to enable replacing add button with remove button instead of hiding the record
				// entry.learning.vocabulary_knowledge.new = entry.learning.vocabulary_knowledge.new.map(x =>
				// 	// @ts-ignore
				// 	x.client_id === data.client_id ? { ...x, id: data.id } : x
				// );
				entry.learning.vocabulary_knowledge.existing.push(data)
			} else {
				const removeId = (obj: any): any => {
					const objCopy = JSON.parse(JSON.stringify(obj))
					delete objCopy.id
					return objCopy
				}
				entry.learning.vocabulary_knowledge.new = entry.learning.vocabulary_knowledge.new.map((x) =>
					// @ts-ignore
					x.id === data.id ? removeId(x) : x,
				)
				entry.learning.vocabulary_knowledge.existing = entry.learning.vocabulary_knowledge.existing.filter(
					(x) => x.id !== data.id,
				)
			}
			setIsLoading(false)
			updateChatEntryByFieldName('id', entry.id, entry)
			await loadLibraryOverview()
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<div className="py-1 flex">
			<div className="w-3/5">
				<span className="text-base block font-normal">{item.word}</span>
				<small className="text-base text-muted-foreground">{item.translation}</small>
			</div>
			<div className="w-2/5 flex items-center justify-center">
				<div className="flex items-center justify-center w-full">
					<button
						onClick={handleSaveOrRemove}
						className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background active:bg-accent active:text-accent-foreground p-0 h-9 w-full">
						{isLoading ? (
							<LoaderCircle className="animate-spin w-5 h-5" />
						) : !item.id ? (
							<Plus id="addToLib" className="w-5 h-5" />
						) : (
							<Trash2 id="addToLib" className="w-5 h-5" />
						)}
						<label
							htmlFor="addToLib"
							className="text-sm ms-2 font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
							{!item.id ? t('Save') : t('Remove')}
						</label>
					</button>
				</div>
			</div>
		</div>
	)
}

const Learning = ({
	indexInList,
	entry,
	maximized = true,
	onMaximized,
}: ChatEntryComponentProps & { indexInList: number; maximized: boolean; onMaximized: (index: number) => void }) => {
	const { t } = useTranslation()
	const { updateChatEntryByFieldName, loadLibraryOverview } = useGlobalStore()
	const {
		showModal: showAddWordModal,
		setData,
		setMode: setWordModalMode,
		setOnSuccessfulFinalAction,
	} = useWordDetailsModalStore()
	const [isMaximized, setIsMaximized] = useState<boolean>(maximized)

	const handleOpenWordDetailsModal = (libraryVocabulary: LibraryVocabulary) => {
		if (!libraryVocabulary.learning_stage) {
			libraryVocabulary.practiced_count = 0
			libraryVocabulary.learning_stage = 0
			libraryVocabulary.last_practiced_at = null
		}
		setData(libraryVocabulary)
		setOnSuccessfulFinalAction(async (mode: OnSuccessfulFinalActionMode | undefined) => {
			const entryCopy: ChatEntry = JSON.parse(JSON.stringify(entry))
			if (mode === OnSuccessfulFinalActionMode.DELETE) {
				entryCopy.learning.vocabulary_knowledge.existing =
					entryCopy.learning.vocabulary_knowledge.existing.filter((x) => x.id !== libraryVocabulary.id)
			}

			updateChatEntryByFieldName('id', entry.id, entryCopy)
			await loadLibraryOverview()
		})
		setWordModalMode('saved')
		showAddWordModal()
	}

	useEffect(() => {
		if (isMaximized) {
			onMaximized(indexInList)
		}
	}, [isMaximized])

	return (
		<Card className={cn('w-full', !isMaximized ? 'cursor-pointer' : '')} onClick={() => setIsMaximized(true)}>
			<CardHeader className="px-2 pt-0 pb-0 relative h-0">
				<Maximize2 className={cn('absolute end-2 top-4 w-3.5 h-3.5', isMaximized ? 'hidden' : '')} />
				{(entry.learning.vocabulary_knowledge.new.length > 0 ||
					entry.learning.grammar_knowledge.new.length > 0) && (
					<Badge className="text-[.7rem] py-[0.1rem] bg-danred hover:bg-danred text-white uppercase absolute end-3 -top-4">
						{t('New Learning')}
					</Badge>
				)}
			</CardHeader>
			{!isMaximized ? (
				<CardContent className="px-2 pt-1 pb-0">
					{(entry.learning.vocabulary_knowledge.new.length > 0 ||
						entry.learning.vocabulary_knowledge.existing.length > 0) && (
						<>
							<p className="block pb-1 text-sm">{t('Vocabulary')}</p>
							<div className="w-full overflow-x-auto whitespace-nowrap">
								{[
									...entry.learning.vocabulary_knowledge.new,
									...entry.learning.vocabulary_knowledge.existing,
								].map((item, index) => {
									return (
										<Button key={index} className="mb-3 me-3 h-6">
											{item.word}
										</Button>
									)
								})}
							</div>
						</>
					)}
					{(entry.learning.grammar_knowledge.new.length > 0 ||
						entry.learning.grammar_knowledge.existing.length > 0) && (
						<>
							<p className="block pb-1 text-sm">{t('Grammar')}</p>
							<div className="w-full overflow-x-auto whitespace-nowrap">
								{[
									...entry.learning.grammar_knowledge.new,
									...entry.learning.grammar_knowledge.existing,
								].map((item, index) => {
									return (
										<Button
											key={index}
											className="mb-3 me-3 h-auto text-left max-w-60 whitespace-normal">
											{item.grammar_topic}
										</Button>
									)
								})}
							</div>
						</>
					)}
				</CardContent>
			) : (
				<>
					{(entry.learning.vocabulary_knowledge.new.length > 0 ||
						entry.learning.vocabulary_knowledge.existing.length > 0) && (
						<>
							{entry.learning.vocabulary_knowledge.new.length > 0 && (
								<CardContent className="px-4 pt-1 pb-1">
									<h4 className="flex items-center font-semibold tracking-tight text-base">
										<Lightbulb className="inline-flex me-1 w-4 h-4" />
										{t('Vocabulary')}
									</h4>
									<div>
										{entry.learning.vocabulary_knowledge.new.map((item, index) => (
											<NewWord key={index} chatEntry={entry} item={item} />
										))}
									</div>
								</CardContent>
							)}
							{entry.learning.vocabulary_knowledge.existing.length > 0 && (
								<CardFooter className="p-3 py-0 flex flex-col">
									<div className="w-full mb-2 mt-1 text-sm">{t('Already in the library')}</div>
									<div className="w-full overflow-x-auto whitespace-nowrap">
										{entry.learning.vocabulary_knowledge.existing.map((item, index) => {
											return (
												<Button
													onClick={() => handleOpenWordDetailsModal(item)}
													key={index}
													className="mb-3 me-3 h-6">
													{item.word}
												</Button>
											)
										})}
									</div>
								</CardFooter>
							)}
						</>
					)}
					{(entry.learning.grammar_knowledge.new.length > 0 ||
						entry.learning.grammar_knowledge.existing.length > 0) && (
						<>
							{entry.learning.grammar_knowledge.new.length > 0 && (
								<CardContent className="px-4 pt-0 pb-1">
									<h4 className="flex items-center font-semibold tracking-tight text-base">
										<Lightbulb className="inline-flex me-1" width="17" />
										{t('Grammar')}
									</h4>
									<div>
										{entry.learning.grammar_knowledge.new.map((item, index) => (
											<NewGrammar key={index} chatEntry={entry} item={item} />
										))}
									</div>
								</CardContent>
							)}
							{entry.learning.grammar_knowledge.existing.length > 0 && (
								<CardFooter className="p-3 py-0 flex flex-col">
									<div className="w-full mb-2 mt-1 text-sm">{t('Already in the library')}</div>
									<div className="w-full overflow-x-auto whitespace-nowrap">
										{entry.learning.grammar_knowledge.existing.map((item, index) => {
											return (
												<Button key={index} className="mb-3 me-3 h-6">
													{item.grammar_topic}
												</Button>
											)
										})}
									</div>
								</CardFooter>
							)}
						</>
					)}
				</>
			)}
		</Card>
	)
}

const DeleteChatDialog = ({
	chat,
	isDeleteChatModalShown,
	setIsDeleteChatModalShown,
}: {
	chat: Chat
	isDeleteChatModalShown: boolean
	setIsDeleteChatModalShown: (val: boolean) => void
}) => {
	const { t } = useTranslation()
	const { appLanguageCode, availableAppLanguages, loadChatHistoryList } = useGlobalStore()
	const [isDeleteChatLoading, setIsDeleteChatLoading] = useState(false)
	const [deleteRelatedLibraryItems, setDeleteRelatedLibraryItems] = useState(false)
	const { resetChat } = useGlobalStore()

	useEffect(() => {
		setDeleteRelatedLibraryItems(false)
	}, [])

	const deleteChat = async () => {
		setIsDeleteChatLoading(true)
		await api(`/v1/chat/${chat?.id}`, {
			method: 'DELETE',
			body: {
				delete_related_library_items: deleteRelatedLibraryItems,
			},
		})
			.then(async () => {
				await loadChatHistoryList()
			})
			.finally(() => setIsDeleteChatLoading(false))
		setIsDeleteChatModalShown(false)
		resetChat(true)
	}

	return (
		<Dialog open={isDeleteChatModalShown} onOpenChange={setIsDeleteChatModalShown}>
			<DialogContent
				dir={availableAppLanguages[appLanguageCode]?.dir}
				onInteractOutside={(e) => {
					if (isDeleteChatLoading) e.preventDefault()
				}}
				className="p-4 w-[95%] rounded-lg select-none">
				<DialogHeader className="text-start">
					<DialogTitle>{t('Confirm deleting the chat')}</DialogTitle>
					<DialogDescription>
						{t('This action cannot be undone. This will permanently delete this chat.')}
					</DialogDescription>
				</DialogHeader>
				<div className="flex items-center">
					<Checkbox
						id="delete_related_library_items"
						checked={deleteRelatedLibraryItems}
						onCheckedChange={(checked) => setDeleteRelatedLibraryItems(checked as boolean)}
					/>
					<label
						htmlFor="delete_related_library_items"
						className="mx-2 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
						{t('Delete related items from the library')}
					</label>
				</div>
				<DialogFooter className="flex flex-row justify-end">
					<LoadingButton
						onClick={() => deleteChat()}
						type="button"
						variant="destructive"
						className="me-3"
						loading={isDeleteChatLoading}>
						{t('Delete')}
					</LoadingButton>
					<DialogClose asChild>
						<Button type="button" variant="secondary">
							{t('Close')}
						</Button>
					</DialogClose>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

const ChatList = ({ chat, entries, sendMessage }: ChatListProps) => {
	const { t } = useTranslation()
	const {
		user,
		isAwaitingAiResponse,
		latestNewChat,
		chatHistoryList,
		appNewsHtml,
		isChatHistoryLoaded,
		isAuthLoaded,
		chatEntries,
		tips,
		availableAppLanguages,
		appLanguageCode,
		isLoadingChat,
	} = useGlobalStore()
	const [showMinorFocusPoints, setShowMinorFocusPoints] = useState(false)
	const messagesContainerRef = useRef<HTMLDivElement>(null)
	const [allLearnings, setAllLearnings] = useState<ChatEntry[]>([])
	const [isDeleteChatModalShown, setIsDeleteChatModalShown] = useState(false)
	const [userHasInteractedWithMessaging, setUserHasInteractedWithMessaging] = useState(false)
	const prevEntriesCount = usePrevious(entries.length)
	//	const [isMarkChatAsImportantLoading, setIsMarkChatAsImportantLoading] = useState(false)

	//	const markChatAsImportant = async (chatId: number) => {
	//		setIsMarkChatAsImportantLoading(true)
	//		await fetch(`${import.meta.env.VITE_API_URL}/chat/${chatId}/mark-as-important`, {
	//			method: 'POST',
	//			headers: {
	//				'Content-Type': 'application/json',
	//				Accept: 'application/json',
	//				...globalRequestHeaders,
	//			},
	//		})
	//			.then((response) => {
	//				if (!response.ok) {
	//					throw new Error('HTTP status ' + response.status)
	//				}
	//				return response.json()
	//			})
	//			.then((data: Chat) => {
	//				setIsMarkChatAsImportantLoading(false)
	//			})
	//	}

	const shouldShowSubscriptionExpiringNotice = useMemo(() => {
		const givenDate = dayjs.utc(user?.subscription_expires_at)
		const now = dayjs()
		const differenceInDays = givenDate.diff(now, 'days')
		// check if subscription expires in 30
		return differenceInDays < 30 && differenceInDays >= 0
	}, [user?.subscription_expires_at])

	useEffect(() => {
		if (prevEntriesCount !== entries.length) {
			setTimeout(() => {
				scrollToBottom()
			}, 100)
		}
		setAllLearnings(entries.filter((x) => x.learning))
	}, [entries])

	const scrollToBottom = () => {
		if (messagesContainerRef.current) {
			messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight
		}
	}

	const onLearningMaximized = (index: number) => {
		if (!isListItemFullyVisible(index)) {
			scrollToBottom()
		}
	}

	const isListItemFullyVisible = (index: number) => {
		if (!messagesContainerRef.current) return false

		const listRect = messagesContainerRef.current.getBoundingClientRect()
		const itemElement = messagesContainerRef.current.children[index]
		const itemRect = itemElement.getBoundingClientRect()

		return itemRect.top >= listRect.top && itemRect.bottom <= listRect.bottom
	}

	const onUserMessagingInteraction = (messageLength: number, imagesCount: number) => {
		setUserHasInteractedWithMessaging(messageLength > 0 || imagesCount > 0)
	}

	return (
		<div className="w-full overflow-y-auto overflow-x-hidden h-full flex flex-col relative">
			<AnimatePresence>
				{isLoadingChat && (
					<motion.div
						key="chat-loading-spinner"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.15 }}
						className="absolute inset-0 z-20 flex items-center justify-center bg-background/60 backdrop-blur-[1px]">
						<Loader2 className="w-9 h-9 animate-spin" />
					</motion.div>
				)}
			</AnimatePresence>
			<AnimatePresence>
				{chatEntries.length <= 0 && !userHasInteractedWithMessaging && (
					<motion.div
						initial={{ opacity: 0, y: -40 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{
							duration: 0.6,
							ease: 'easeOut',
						}}
						className=" select-none">
						<div className="font-semibold dark:text-white text-black pb-2">
							<Carousel
								className="w-full"
								opts={{
									direction: availableAppLanguages[appLanguageCode]?.dir as 'ltr' | 'rtl',
								}}>
								<CarouselContent>
									{tips.map((tip, index) => (
										<CarouselItem key={index} className="h-28 px-2 cursor-pointer">
											<div className="p-1 h-full">
												<Card className="h-full bg-background/85 border-0">
													<CardContent className="flex flex-col items-center justify-center px-0 py-2 h-full">
														<h3 className="text-sm font-semibold text-center">
															{t(tip.title)}
														</h3>
														<p className="text-sm mt-2 text-gray-500 text-center">
															{t(tip.text)}
														</p>
													</CardContent>
												</Card>
											</div>
										</CarouselItem>
									))}
								</CarouselContent>
								<CarouselDots className="mt-2" />
							</Carousel>
						</div>
						{/*{isChatHistoryLoaded && chatHistoryList.length <= 0 && (*/}
						{/*	<div className="py-2">*/}
						{/*		{t(*/}
						{/*			'Have you seen a strange phrase somewhere? Do you need to clarify a grammar rule? Translate something? Get started here!',*/}
						{/*		)}*/}
						{/*	</div>*/}
						{/*)}*/}
						<div className="flex flex-col gap-2 my-2 mx-7">
							{isAuthLoaded && (user === null || user.trial_expires_at !== null) && (
								<Button
									onClick={() => dispatch('show-auth-dialog')}
									variant="secondary"
									className="border-2 px-3 border-gray-500">
									<UserPlus className="w-4 h-4 me-2" />
									{t('Create free account / Sign in')}
								</Button>
							)}
							{(user?.subscription_expires_at === null ||
								dayjs.utc(user?.subscription_expires_at) < dayjs().utc() ||
								(user?.unsubscribed_at !== null &&
									dayjs.utc(user?.subscription_expires_at) > dayjs().utc())) && (
								<RainbowButton
									onClick={() =>
										dispatch('toggle-purchase-subscription-dialog', {
											val: true,
											mode: '',
										})
									}
									className="rounded-md font-medium text-sm px-3 h-10">
									<Rocket className="w-4 h-4 me-2" />
									{t('Upgrade to unlimited access')}
								</RainbowButton>
							)}
							{isAuthLoaded && (
								<>
									{user !== null && (
										<div className="flex flex-col justiy-center items-center dark:text-white text-black text-sm gap-2">
											{user.subscription_expires_at !== null &&
												dayjs.utc(user?.subscription_expires_at) < dayjs().utc() && (
													<span>{t('Unlimited Access expired')}</span>
												)}
											{user.unsubscribed_at &&
												dayjs.utc(user?.subscription_expires_at) > dayjs().utc() &&
												shouldShowSubscriptionExpiringNotice && (
													<span>{`${t('Subscription expiring:')} ${dayjs.utc(user?.subscription_expires_at).local().fromNow()}`}</span>
												)}
										</div>
									)}
									{(user === null || user.trial_expires_at !== null) && (
										<div className="dark:text-white text-black text-sm flex justify-center gap-2">
											<span>
												{t(
													dayjs.utc(user?.trial_expires_at) < dayjs().utc()
														? 'Trial expired'
														: 'Trial expiring',
												)}
												:
											</span>
											<span>{dayjs().to(dayjs.utc(user?.trial_expires_at).local())}</span>
										</div>
									)}
								</>
							)}
						</div>
						<div dangerouslySetInnerHTML={{ __html: appNewsHtml }}></div>
					</motion.div>
				)}
			</AnimatePresence>
			<div
				ref={messagesContainerRef}
				className="w-full xl:px-60 lg:px-32 overflow-y-auto overflow-x-hidden h-full flex flex-col pb-8"
				style={{
					// This gradient makes the top 30% fade out:
					WebkitMaskImage: 'linear-gradient(to bottom, black 95%, transparent 100%)',
					maskImage: 'linear-gradient(to bottom, black 95%, transparent 100%)',
				}}>
				<AnimatePresence initial={false}>
					{chat?.title && (
						<div className="px-2 py-2 mb-5 flex gap-2">
							<DropdownMenu
								dir={availableAppLanguages[appLanguageCode]?.dir as 'ltr' | 'rtl'}
								modal={false}>
								<DropdownMenuTrigger asChild>
									<div className="select-none flex items-center justify-center rounded-full border h-6 w-6 text-xs font-semibold transition-colors active:outline-none border-transparent bg-primary text-primary-foreground active:bg-primary/80">
										<EllipsisVertical className="h-5 w-5 cursor-pointer" />
									</div>
								</DropdownMenuTrigger>
								<DropdownMenuContent>
									<DropdownMenuLabel>{t('Chat')}</DropdownMenuLabel>
									<DropdownMenuSeparator />
									{/*<DropdownMenuItem>*/}
									{/*	<Star />*/}
									{/*	<span>{t('Mark as important')}</span>*/}
									{/*</DropdownMenuItem>*/}
									<DropdownMenuItem
										className="cursor-pointer"
										onSelect={() => setIsDeleteChatModalShown(true)}>
										<Trash2 />
										<span>{t('Delete chat')}</span>
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
							<DeleteChatDialog
								chat={chat}
								isDeleteChatModalShown={isDeleteChatModalShown}
								setIsDeleteChatModalShown={setIsDeleteChatModalShown}
							/>
							{chat?.focus && chat?.focus != 'Other' && (
								<div
									onClick={() => setShowMinorFocusPoints(!showMinorFocusPoints)}
									className="select-none start-10 inline-flex items-center rounded-full border px-2.5 h-6 text-xs font-semibold transition-colors active:outline-none border-transparent bg-primary text-primary-foreground active:bg-primary/80">
									<span className="text-sm whitespace-nowrap">{chat?.focus}</span>
									{chat?.focus_minor?.length > 0 && !showMinorFocusPoints && (
										<PlusCircle className="ml-1" width="15" />
									)}
									{showMinorFocusPoints &&
										chat?.focus_minor?.map((x, i) => (
											<span className="mx-1" key={i}>
												+ {x}
											</span>
										))}
								</div>
							)}
						</div>
					)}
					{entries?.map((entry, index) => (
						<motion.div
							key={index}
							layout
							initial={{ opacity: 0, scale: 1, y: 50, x: 0 }}
							animate={{ opacity: 1, scale: 1, y: 0, x: 0 }}
							exit={{ opacity: 0, scale: 1, y: 1, x: 0 }}
							transition={{
								opacity: { duration: 0.1 },
								layout: {
									type: 'spring',
									bounce: 0.2,
									duration: entries.indexOf(entry) * 0.05 + 0.2,
								},
							}}
							style={{
								originX: 0.5,
								originY: 0.5,
							}}
							className={cn(
								'flex flex-col gap-2 p-2',
								entry.user_id !== user?.id ? 'items-left' : 'items-right',
								isAwaitingAiResponse && index === entries.length - 1 ? 'animate-pulse' : '',
							)}>
							<div
								className={cn(
									'flex gap-2 items-baseline',
									entry.learning !== undefined ? 'w-full' : '',
								)}>
								{(entry.message !== undefined && <Message entry={entry} user={user} />) ||
									(entry.learning !== undefined &&
										allLearnings.length > 0 &&
										(entry.learning.vocabulary_knowledge.new.length > 0 ||
											entry.learning.vocabulary_knowledge.existing.length > 0 ||
											entry.learning.grammar_knowledge.new.length > 0 ||
											entry.learning.grammar_knowledge.existing.length > 0) && (
											<Learning
												indexInList={index}
												entry={entry}
												user={user}
												maximized={latestNewChat?.id == chat?.id}
												onMaximized={onLearningMaximized}
											/>
										))}
							</div>
						</motion.div>
					))}
				</AnimatePresence>
			</div>
			{chatEntries.length <= 0 && !userHasInteractedWithMessaging && (
				<div className="flex flex-col items-center px-7 py-2 gap-5 relative">
					{isChatHistoryLoaded && chatHistoryList.length <= 0 && (
						<div className="w-full">
							{t(
								'Start by asking a language related question or visit the library tab and add your own vocabulary',
							)}
						</div>
					)}
					<div className="w-full font-semibold text-md flex items-center justify-center">
						<Sparkles className="w-4 h-4 me-1" /> {t('Ask LingoFlow AI')}
					</div>
					<ArrowDown className="w-5 h-5 animate-bounce" />
				</div>
			)}
			<ChatBottomBar sendMessage={sendMessage} onUserMessagingInteraction={onUserMessagingInteraction} />
			<DotPattern
				width={20}
				height={20}
				cx={1}
				cy={1}
				cr={1}
				className={cn('[mask-image:linear-gradient(to_bottom_right,white,transparent,transparent)] z-[-1]')}
			/>
		</div>
	)
}

export default ChatList
